/*
  Provides functionality for "on click outside of element(s)" -
  i.e. Common use-case for this is closing pop-out elements.
  The desired UX for most pop outs is to close them when a user
  clicks outside of the element.

  This module relies on having access to jQuery.

  @param onClickOutside [Function] function to call when "clicked outside" event happens.
  @param ignoreElems [Array] array of element selector strings to ignore for triggering behavior.

  Usage:
  ```
  clickOutside = make(OnClickOutside, {
    ignoreElems: [".toggle-button", ".popout-container"],
    onClickOutside: () => {
      tether.hideAll()
      clickOutside.stopListening()
    },
  })
  ```
*/

import $ from "jquery";
import { randomString } from "../utils";

const clickHandlers = new Map();

/*
 * TLDR - This is needed until the whole app is written in React.
 * This module uses a single click event listener attached to the document.
 * The reason we favor this approach over adding/removing click event listeners for each handler is
 * because of React. React uses delegated event listeners on the document
 * for all events. If we are adding/removing listeners they will be added after the React listener!
 * This means there's potential for a React re-render before the event reaches OnClickOutside.
 * This could be problematic since we rely on the event target being in the DOM.
 * Hence why we add the listener here and ASSUME this is ran before React starts up.
 */
if (typeof document !== "undefined") {
  $(document).on("click", (event: any) => {
    clickHandlers.forEach((handler) => {
      handler(event);
    });
  });
}

export default class OnClickOutside {
  private handlerId: string;
  private ignoreElemsSelector: string;

  constructor(ignoreElems: string[], private onClickOutside: () => any) {
    this.handlerId = randomString();
    this.ignoreElemsSelector = ignoreElems.join(",");
  }

  /*
    We make the stop / start listeners public so that we can instantiate this class once
    for a relevant piece of DOM and attach and remove listeners as its added and removed
    from the DOM.
  */
  public startListening() {
    clickHandlers.set(this.handlerId, ($event: any) => {
      if (this.outsideIgnorableElems($event.target)) {
        this.stopListening();
        return this.onClickOutside();
      }
    });
  }

  /**
   * @NOTE(blambo, 2019-06-19): Since we use delegated listeners, if we remove the
   * relevant DOM then we want to kill the listeners we created. So we provide this
   * programmatic access so that the caller can destroy the listeners.
   */
  public stopListening() {
    clickHandlers.delete(this.handlerId);
  }

  // Determines if click event's target was OUTSIDE of the ignored elements.
  private outsideIgnorableElems(target: any) {
    const targetElem = $(target);
    return !targetElem.is(this.ignoreElemsSelector) && targetElem.parents(this.ignoreElemsSelector).length === 0;
  }
}
